import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import { Button } from "@material-ui/core";
import CSPhoneInput from "../Common/CSPhoneInput";
import { useCSMessage } from "../Common/CSMessage";
import { useCSDialog } from "../Common/CSDialog";
import { submitNewGroupPlan, getPlanBenefits } from "../../api/groupApi";

// ─── Constants ────────────────────────────────────────────────────────────────

const CURRENT_YEAR = new Date().getFullYear();
const PLAN_YEARS = [CURRENT_YEAR, CURRENT_YEAR + 1];

const AFFILIATE_SPONSORS = [
  "Blue Cross Blue Shield of Minnesota",
  "Wellmark Blue Cross Blue Shield of South Dakota",
  "Wellmark Blue Cross Blue Shield of Iowa",
  "Blue Cross Blue Shield of North Dakota",
  "Blue Cross Blue Shield of Nebraska",
];

const SPONSOR_TYPES = ["Employer", "Union", "Trustees of a fund"];

const ORG_TYPES = [
  "State Government",
  "Local Government",
  "Publicly Traded Organization",
  "Privately Health Organization",
  "Non Profit",
  "Church Group",
  "Other",
];

const CONTRACT_TYPES = ["Insured", "ASO"];

const PBS_OPTIONS = [
  { value: "10-25-40-25", label: "$10/$25/$40/25%" },
  { value: "custom", label: "Custom" },
];

const ENROLLMENT_METHODS = ["Paper Enrollment", "Electronic File"];

const PAYMENT_OPTIONS = ["Split Bill", "Bill to Member", "Bill to Group"];

const HQ_STATES = [
  { value: "IA", label: "Iowa" },
  { value: "MN", label: "Minnesota" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "ND", label: "North Dakota" },
  { value: "SD", label: "South Dakota" },
  { value: "WY", label: "Wyoming" },
];

// ─── Styles ───────────────────────────────────────────────────────────────────

const sectionStyle = {
  padding: "20px",
  border: "1px solid #e0e0e0",
  borderRadius: "6px",
  backgroundColor: "#fafbfd",
  marginTop: "32px",
};

const sectionHeadingStyle = {
  color: "#0056a3",
  marginTop: 0,
  marginBottom: "16px",
};

const noteStyle = {
  background: "#f0f7ff",
  borderLeft: "3px solid #0072C6",
  padding: "8px 10px",
  fontSize: "13px",
  marginTop: "6px",
};

const hintStyle = {
  fontSize: "12px",
  color: "#666",
  marginTop: "4px",
};

const subtitleStyle = {
  fontSize: "13px",
  color: "#555",
  marginBottom: "16px",
};

// ─── Default values ───────────────────────────────────────────────────────────

const defaultValues = {
  // Group Information
  planYear: "",
  affiliatedSponsor: "",
  groupLegalName: "",
  groupDBAName: "",
  groupNumber: "",
  employerFedTaxId: "",
  employerSponsorType: "",
  employerOrgType: "",
  employerContractType: "",
  numExpectedEnrollees: "",
  requestedEffectiveDate: "",
  // Plan Information
  planBenefitStructure: "",
  pbsCustomization: "",
  enrollmentCustomization: "",
  openEnrollmentStartDate: "",
  enrollmentRequestMethod: "",
  paymentOption: "",
  employerGroupPremiumAmount: "",
  lateMemberPayIndicator: false,
  totalPremiumAmount: "",
  rds1: "",
  rds2: "",
  rds3: "",
  creditableDrugCoverage: "",
  // Broker Information
  brokerFirstName: "",
  brokerLastName: "",
  brokerNumber: "",
  brokerPhone: "",
  agencyNumber: "",
  // Primary Billing Contact
  pbcFirstName: "",
  pbcLastName: "",
  pbcEffectiveDate: "",
  pbcExpirationDate: "",
  pbcPhone: "",
  pbcEmail: "",
  // Employer Headquarters Contact
  hqFirstName: "",
  hqLastName: "",
  hqEffectiveDate: "",
  hqExpirationDate: "",
  hqAddress1: "",
  hqAddress2: "",
  hqCity: "",
  hqState: "",
  hqZip: "",
  hqPhone: "",
  hqEmail: "",
};

// ─── Helper ───────────────────────────────────────────────────────────────────

const ErrorRow = ({ errors, name }) => (
  <ErrorMessage
    errors={errors}
    name={name}
    render={({ message }) => (
      <div role="alert" aria-live="polite" className="error">{message}</div>
    )}
  />
);

// ─── Component ────────────────────────────────────────────────────────────────

const GroupImplementation = () => {
  const {
    register,
    control,
    handleSubmit,
    watch,
    getValues,
    clearErrors,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues,
  });

  // ── Conditional watches ──────────────────────────────────────────────────
  const watchPBS     = watch("planBenefitStructure");
  const watchPayment = watch("paymentOption");
  const watchRDS1    = watch("rds1");
  const watchRDS2    = watch("rds2");
  const watchCred    = watch("creditableDrugCoverage");

  // Phone watches (needed for CSPhoneInput value prop)
  const watchBrokerPhone = watch("brokerPhone");
  const watchPbcPhone    = watch("pbcPhone");
  const watchHqPhone     = watch("hqPhone");

  // ── State ────────────────────────────────────────────────────────────────
  const [isProcessing, setIsProcessing] = useState(false);
  const [isConfirmed, setIsConfirmed]   = useState(false);
  const [oeEnabled, setOeEnabled]       = useState(false);
  const [oePending, setOePending]       = useState(false);
  const [planBenefits, setPlanBenefits] = useState([]);
  const [showMessage, csMessage] = useCSMessage();
  const [showDialog, csDialog] = useCSDialog();

  const watchPlanYear = watch("planYear");
  useEffect(() => {
    if (!watchPlanYear) { setPlanBenefits([]); return; }
    setValue("planBenefitStructure", "");
    clearErrors("planBenefitStructure");
    getPlanBenefits(watchPlanYear)
      .then((benefits) => {
        setPlanBenefits(Array.isArray(benefits) ? benefits : []);
      })
      .catch(() => setPlanBenefits([]));
  }, [watchPlanYear]);

  // ── Derived flags ────────────────────────────────────────────────────────
  const isCustomPBS = watchPBS     === "custom";
  const isSplitBill = watchPayment === "Split Bill";
  const showRDS2    = watchRDS1    === "No";
  const showRDS3    = watchRDS1    === "No" && watchRDS2 === "No";

  // ── Open Enrollment handlers ─────────────────────────────────────────────
  const handleOeCheckbox = async (e) => {
    if (e.target.checked) {
      setOePending(true);
      const result = await showDialog({
        mode: "confirmation",
        title: "New Group Implementation",
        confirmText: "I acknowledge",
        cancelText: "Cancel",
        message: (
          <p>
            <strong>ANOCs</strong> are required to be in member hands{" "}
            <strong>15 days prior</strong> to the open enrollment start date.
            Please acknowledge to proceed.
          </p>
        ),
      });
      if (result !== null) {
        setOeEnabled(true);
      }
      setOePending(false);
    } else {
      setOeEnabled(false);
      setOePending(false);
      setValue("openEnrollmentStartDate", "");
    }
  };

  // ── Submit ───────────────────────────────────────────────────────────────
  const onSubmit = async (data) => {
    setIsProcessing(true);
    try {
      //const payload = { ...data };
      const payload = {
        planYear:                   data.planYear ? parseInt(data.planYear, 10) : null,
        affiliatedSponsor:          data.affiliatedSponsor || null,
        groupLegalName:             data.groupLegalName || null,
        groupDBAName:               data.groupDBAName || null,
        groupNumber:                data.groupNumber || null,
        employerFedTaxId:           data.employerFedTaxId || null,
        employerSponsorType:        data.employerSponsorType || null,
        employerOrgType:            data.employerOrgType || null,
        employerContractType:       data.employerContractType || null,
        numExpectedEnrollees:       data.numExpectedEnrollees?.toString() || null,
        requestedEffectiveDate:     data.requestedEffectiveDate || null,
        planBenefitStructure:       data.planBenefitStructure || null,
        planBenefitId:              data.planBenefitStructure || null,
        pbsCustomization:           data.pbsCustomization || null,
        enrollmentCustomization:    data.enrollmentCustomization || null,
        openEnrollmentStartDate:    oeEnabled ? (data.openEnrollmentStartDate || null) : null,
        enrollmentRequestMethod:    data.enrollmentRequestMethod || null,
        paymentOption:              data.paymentOption || null,
        employerGroupPremiumAmount: data.employerGroupPremiumAmount !== "" ? Number(data.employerGroupPremiumAmount) : null,
        lateMemberPayIndicator:     data.lateMemberPayIndicator,
        totalPremiumAmount:         data.totalPremiumAmount !== "" ? Number(data.totalPremiumAmount) : null,
        rds1:                       data.rds1 || null,
        rds2:                       data.rds2 || null,
        rds3:                       data.rds3 || null,
        creditableDrugCoverage:     data.creditableDrugCoverage || null,
        brokerFirstName:            data.brokerFirstName || null,
        brokerLastName:             data.brokerLastName || null,
        brokerNumber:               data.brokerNumber || null,
        brokerPhone:                data.brokerPhone || null,
        agencyNumber:               data.agencyNumber || null,
        pbcFirstName:               data.pbcFirstName || null,
        pbcLastName:                data.pbcLastName || null,
        pbcPhone:                   data.pbcPhone || null,
        pbcEmail:                   data.pbcEmail || null,
        pbcEffectiveDate:           data.pbcEffectiveDate || null,
        pbcExpirationDate:          data.pbcExpirationDate || null,
        hqFirstName:                data.hqFirstName || null,
        hqLastName:                 data.hqLastName || null,
        hqAddress1:                 data.hqAddress1 || null,
        hqAddress2:                 data.hqAddress2 || null,
        hqCity:                     data.hqCity || null,
        hqState:                    data.hqState || null,
        hqZip:                      data.hqZip || null,
        hqPhone:                    data.hqPhone || null,
        hqEmail:                    data.hqEmail || null,
        hqEffectiveDate:            data.hqEffectiveDate || null,
        hqExpirationDate:           data.hqExpirationDate || null,
      };
      await submitNewGroupPlan(payload);
      setIsConfirmed(true);
    } catch (err) {
      await showMessage({
        message: `An error occurred while submitting your request: ${err.message}`,
        title: "New Group Implementation",
        messageType: "danger",
      });
    }
    setIsProcessing(false);
  };

  // ── Confirmation screen ──────────────────────────────────────────────────
  if (isConfirmed) {
    const submittedGroupNumber = getValues("groupNumber");
    return (
      <div role="main" className="main-container pt-md-5 pb-5 container js-quickedit-main-content">
        <div className="row d-md-flex">
          <section className="col-sm-12 internal-page">
            <div className="region region-content">

              {/* Breadcrumb */}
              <section className="block block-clearstone-updates block-clearstone-breadcrumbs-block clearfix">
                <div className="breadcrumb-wrap">
                  <div className="region--breadcrumb nst-1">
                    <section className="block-system block-system-breadcrumb-block">
                      <div role="navigation">
                        <ol className="breadcrumb">
                          <li><Link to="/">Home</Link></li>
                          <li><Link to="/GroupPlanManagement">Group Plan Management</Link></li>
                          <li>New Group Implementation</li>
                        </ol>
                      </div>
                    </section>
                  </div>
                </div>
              </section>

              <h1 className="page-header">New Group Implementation</h1>
              <div style={{ padding: "24px", border: "1px solid #d4edda", borderRadius: "6px", background: "#f0f9f0", textAlign: "center" }}>
                <div style={{ fontSize: "56px", color: "#16a34a", lineHeight: 1 }}>✓</div>
                <h2 style={{ color: "#0056a3" }}>New Group Submission Received</h2>
                <p>Thank you. Your new group implementation request has been submitted successfully.</p>
                <div style={{ display: "flex", justifyContent: "center", gap: "16px", marginTop: "16px", flexWrap: "wrap" }}>
                  <Button
                    className="webform-button--next button js-form-submit form-submit btn-default btn"
                    onClick={() => {
                      setIsConfirmed(false);
                      setOeEnabled(false);
                      reset(defaultValues);
                    }}
                  >
                    Submit Another Request
                  </Button>
                  <Button
                    className="webform-button--next button js-form-submit form-submit btn-default btn"
                    onClick={() => window.location.href = `/GroupPlanContactInfo?groupNumber=${submittedGroupNumber}`}
                  >
                    Manage Group Contacts
                  </Button>
                  <Button
                    className="webform-button--next button js-form-submit form-submit btn-default btn"
                    onClick={() => window.location.href = "/GroupLogin"}
                  >
                    Group Plan Management
                  </Button>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    );
  }

  // ── Form ─────────────────────────────────────────────────────────────────
  return (
    <div role="main" className="main-container pt-md-5 pb-5 container js-quickedit-main-content">
      <div className="row d-md-flex">
        <section className="col-sm-12 internal-page">
          <div className="region region-content">

            {/* Breadcrumb */}
            <section className="block block-clearstone-updates block-clearstone-breadcrumbs-block clearfix">
              <div className="breadcrumb-wrap">
                <div className="region--breadcrumb nst-1">
                  <section className="block-system block-system-breadcrumb-block">
                    <div role="navigation">
                      <ol className="breadcrumb">
                        <li><Link to="/">Home</Link></li>
                        <li><Link to="/GroupPlanManagement">Group Plan Management</Link></li>
                        <li>New Group Implementation</li>
                      </ol>
                    </div>
                  </section>
                </div>
              </div>
            </section>

            <h1 className="page-header">New Group Implementation</h1>

            <form onSubmit={handleSubmit(onSubmit)} noValidate>

              {/* ══════════════════════════════════════════════════════════════
                  GROUP INFORMATION
              ══════════════════════════════════════════════════════════════ */}
              <div style={sectionStyle}>
                <h2 style={sectionHeadingStyle}>Group Information</h2>

                {/* Plan Year · Affiliated Sponsor */}
                <div className="row form-group">
                  <div className="col-sm-6 col-md-6 form-item form-group">
                    <label htmlFor="planYear" className="control-label js-form-required form-required">Plan Year</label>
                    <select id="planYear" className="form-select form-control"
                      {...register("planYear", { required: "Plan Year is required." })}>
                      <option value="">Select</option>
                      {PLAN_YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
                    </select>
                    <ErrorRow errors={errors} name="planYear" />
                  </div>
                  <div className="col-sm-6 col-md-6 form-item form-group">
                    <label htmlFor="affiliatedSponsor" className="control-label js-form-required form-required">Affiliated Health Plan Sponsor</label>
                    <select id="affiliatedSponsor" className="form-select form-control"
                      {...register("affiliatedSponsor", { required: "Affiliated Health Plan Sponsor is required." })}>
                      <option value="">Select</option>
                      {AFFILIATE_SPONSORS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <ErrorRow errors={errors} name="affiliatedSponsor" />
                  </div>
                </div>

                {/* Legal Name · DBA Name */}
                <div className="row form-group">
                  <div className="col-sm-6 col-md-6 form-item form-group">
                    <label htmlFor="groupLegalName" className="control-label js-form-required form-required">Employer Group Legal Name</label>
                    <input id="groupLegalName" type="text" className="form-text required form-control"
                      {...register("groupLegalName", { required: "Employer Group Legal Name is required." })} />
                    <ErrorRow errors={errors} name="groupLegalName" />
                  </div>
                  <div className="col-sm-6 col-md-6 form-item form-group">
                    <label htmlFor="groupDBAName" className="control-label">Employer Group DBA Name</label>
                    <input id="groupDBAName" type="text" className="form-text form-control"
                      {...register("groupDBAName")} />
                    <ErrorRow errors={errors} name="groupDBAName" />
                  </div>
                </div>

                {/* Group Number · Federal Tax ID */}
                <div className="row form-group">
                  <div className="col-sm-6 col-md-6 form-item form-group">
                    <label htmlFor="groupNumber" className="control-label js-form-required form-required">Group Number</label>
                    <input id="groupNumber" type="text" autoComplete="off" spellCheck="false"
                      className="form-text required form-control"
                      {...register("groupNumber", {
                        required: "Group Number is required.",
                        pattern: { value: /^\S+$/, message: "Group Number must not contain spaces." },
                      })} />
                    <ErrorRow errors={errors} name="groupNumber" />
                  </div>
                  <div className="col-sm-6 col-md-6 form-item form-group">
                    <label htmlFor="employerFedTaxId" className="control-label js-form-required form-required">Employer Federal Tax ID</label>
                    <input id="employerFedTaxId" type="text" inputMode="numeric"
                      className="form-text required form-control"
                      {...register("employerFedTaxId", {
                        required: "Employer Federal Tax ID is required.",
                        pattern: { value: /^\d+$/, message: "Federal Tax ID must contain digits only, no hyphens." },
                      })}
                      onInput={(e) => { e.target.value = e.target.value.replace(/[^\d]/g, ""); }}
                    />
                    {/* <div style={hintStyle}>Digits only; no hyphens.</div> */}
                    <ErrorRow errors={errors} name="employerFedTaxId" />
                  </div>
                </div>

                {/* Sponsor Type · Org Type */}
                <div className="row form-group">
                  <div className="col-sm-6 col-md-6 form-item form-group">
                    <label htmlFor="employerSponsorType" className="control-label js-form-required form-required">Employer Sponsor Type</label>
                    <select id="employerSponsorType" className="form-select form-control"
                      {...register("employerSponsorType", { required: "Employer Sponsor Type is required." })}>
                      <option value="">Select</option>
                      {SPONSOR_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <ErrorRow errors={errors} name="employerSponsorType" />
                  </div>
                  <div className="col-sm-6 col-md-6 form-item form-group">
                    <label htmlFor="employerOrgType" className="control-label js-form-required form-required">Employer Organization Type</label>
                    <select id="employerOrgType" className="form-select form-control"
                      {...register("employerOrgType", { required: "Employer Organization Type is required." })}>
                      <option value="">Select</option>
                      {ORG_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <ErrorRow errors={errors} name="employerOrgType" />
                  </div>
                </div>

                {/* Contract Type · Expected Enrollees */}
                <div className="row form-group">
                  <div className="col-sm-6 col-md-6 form-item form-group">
                    <label htmlFor="employerContractType" className="control-label js-form-required form-required">Employer Contract Type</label>
                    <select id="employerContractType" className="form-select form-control"
                      {...register("employerContractType", { required: "Employer Contract Type is required." })}>
                      <option value="">Select</option>
                      {CONTRACT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <ErrorRow errors={errors} name="employerContractType" />
                  </div>
                  <div className="col-sm-6 col-md-6 form-item form-group">
                    <label htmlFor="numExpectedEnrollees" className="control-label">Number of Expected Enrollees</label>
                    <input id="numExpectedEnrollees" type="number" min="0" step="1"
                      className="form-text form-control"
                      {...register("numExpectedEnrollees", {
                        validate: (val) => {
                          if (val === "" || val === null || val === undefined) return true;
                          const n = Number(val);
                          if (isNaN(n)) return "Must be a valid number.";
                          if (n < 0) return "Must be 0 or greater.";
                          return true;
                        },
                      })} />
                    <ErrorRow errors={errors} name="numExpectedEnrollees" />
                  </div>
                </div>

                {/* Requested Effective Date */}
                <div className="row form-group">
                  <div className="col-sm-6 col-md-6 form-item form-group">
                    <label htmlFor="requestedEffectiveDate" className="control-label js-form-required form-required">Requested Effective Date</label>
                    <input id="requestedEffectiveDate" type="date" className="form-text required form-control"
                      {...register("requestedEffectiveDate", { required: "Requested Effective Date is required." })} />
                    <ErrorRow errors={errors} name="requestedEffectiveDate" />
                  </div>
                  <div className="col-sm-6 col-md-6" />
                </div>
              </div>

              {/* ══════════════════════════════════════════════════════════════
                  PLAN INFORMATION
              ══════════════════════════════════════════════════════════════ */}
              <div style={sectionStyle}>
                <h2 style={sectionHeadingStyle}>Plan Information</h2>

                {/* Plan Benefit Structure */}
                <div className="row form-group">
                  <div className="col-sm-6 col-md-6 form-item form-group">
                    <label htmlFor="planBenefitStructure" className="control-label js-form-required form-required">Plan Benefit Structure</label>
                    <select id="planBenefitStructure" className="form-select form-control"
                      {...register("planBenefitStructure", {
                        required: "Plan Benefit Structure is required.",
                        onChange: (e) => {
                          if (e.target.value !== "custom") {
                            setValue("pbsCustomization", "");
                            setValue("enrollmentCustomization", "");
                            clearErrors(["pbsCustomization", "enrollmentCustomization"]);
                          }
                        },
                      })}>
                      <option value="">Select</option>
                      {Array.isArray(planBenefits) && planBenefits.map((b) => <option key={b.value} value={b.value}>{b.text}</option>)}
                    </select>
                    {/* <div style={hintStyle}>Values are populated annually via maintenance screen; sample option shown.</div> */}
                    <ErrorRow errors={errors} name="planBenefitStructure" />
                  </div>
                  <div className="col-sm-6 col-md-6" />
                </div>

                {/* PBS Customization · Enrollment Customization (Custom PBS only) */}
                {isCustomPBS && (
                  <div className="row form-group">
                    <div className="col-sm-6 col-md-6 form-item form-group">
                      <label htmlFor="pbsCustomization" className="control-label js-form-required form-required">PBS Customization</label>
                      <input id="pbsCustomization" type="text" className="form-text required form-control"
                        {...register("pbsCustomization", {
                          validate: (val) =>
                            getValues("planBenefitStructure") === "custom" && !val?.trim()
                              ? "PBS Customization is required."
                              : true,
                        })} />
                      <ErrorRow errors={errors} name="pbsCustomization" />
                    </div>
                    <div className="col-sm-6 col-md-6 form-item form-group">
                      <label htmlFor="enrollmentCustomization" className="control-label js-form-required form-required">Enrollment Customization</label>
                      <input id="enrollmentCustomization" type="text" className="form-text required form-control"
                        {...register("enrollmentCustomization", {
                          validate: (val) =>
                            getValues("planBenefitStructure") === "custom" && !val?.trim()
                              ? "Enrollment Customization is required."
                              : true,
                        })} />
                      <ErrorRow errors={errors} name="enrollmentCustomization" />
                    </div>
                  </div>
                )}

                {/* Open Enrollment Start Date (checkbox-gated, requires acknowledgement) */}
                <div className="row form-group">
                  <div className="col-sm-6 col-md-6 form-item form-group">
                    <label style={{ display: "flex", alignItems: "center", gap: "8px", fontWeight: 600 }}>
                      <input
                        type="checkbox"
                        checked={oeEnabled || oePending}
                        onChange={handleOeCheckbox}
                      />
                      Open Enrollment Start Date
                    </label>
                    <div style={hintStyle}>Requires acknowledgement before selecting a date.</div>
                    {oeEnabled && (
                      <input
                        id="openEnrollmentStartDate"
                        type="date"
                        className="form-text form-control"
                        style={{ marginTop: "8px" }}
                        {...register("openEnrollmentStartDate")}
                      />
                    )}
                  </div>
                  <div className="col-sm-6 col-md-6" />
                </div>

                {/* Enrollment Request Method · Payment Option */}
                <div className="row form-group">
                  <div className="col-sm-6 col-md-6 form-item form-group">
                    <label htmlFor="enrollmentRequestMethod" className="control-label js-form-required form-required">Enrollment Request Method</label>
                    <select id="enrollmentRequestMethod" className="form-select form-control"
                      {...register("enrollmentRequestMethod", { required: "Enrollment Request Method is required." })}>
                      <option value="">Select</option>
                      {ENROLLMENT_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                    <ErrorRow errors={errors} name="enrollmentRequestMethod" />
                  </div>
                  <div className="col-sm-6 col-md-6 form-item form-group">
                    <label htmlFor="paymentOption" className="control-label js-form-required form-required">Payment Option (Billing Method)</label>
                    <select id="paymentOption" className="form-select form-control"
                      {...register("paymentOption", {
                        required: "Payment Option is required.",
                        onChange: (e) => {
                          if (e.target.value !== "Split Bill") {
                            setValue("employerGroupPremiumAmount", "");
                            clearErrors("employerGroupPremiumAmount");
                          }
                        },
                      })}>
                      <option value="">Select</option>
                      {PAYMENT_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                    <ErrorRow errors={errors} name="paymentOption" />
                  </div>
                </div>

                {/* Employer Group Premium Amount (Split Bill only) */}
                {isSplitBill && (
                  <div className="row form-group">
                    <div className="col-sm-6 col-md-6 form-item form-group">
                      <label htmlFor="employerGroupPremiumAmount" className="control-label js-form-required form-required">Employer Group Premium Amount</label>
                      <input id="employerGroupPremiumAmount" type="number" step="0.01" min="0"
                        className="form-text required form-control"
                        {...register("employerGroupPremiumAmount", {
                          validate: (val) => {
                            if (getValues("paymentOption") !== "Split Bill") return true;
                            if (val === "" || val === undefined || val === null) return "Employer Group Premium Amount is required.";
                            if (Number(val) < 0) return "Amount must be 0 or greater.";
                            return true;
                          },
                        })} />
                      <div style={hintStyle}>Required when Billing Method is Split Bill.</div>
                      <ErrorRow errors={errors} name="employerGroupPremiumAmount" />
                    </div>
                    <div className="col-sm-6 col-md-6" />
                  </div>
                )}

                {/* Late Enrollment Indicator · Total Premium Amount */}
                <div className="row form-group">
                  <div className="col-sm-6 col-md-6 form-item form-group" style={{ display: "flex", alignItems: "center", paddingTop: "26px" }}>
                    <label style={{ display: "flex", alignItems: "center", gap: "8px", fontWeight: 600, margin: 0 }}>
                      <input type="checkbox" {...register("lateMemberPayIndicator")} />
                      Late Enrollment Member Pay Indicator
                    </label>
                  </div>
                  <div className="col-sm-6 col-md-6 form-item form-group">
                    <label htmlFor="totalPremiumAmount" className="control-label js-form-required form-required">Total Premium Amount</label>
                    <input id="totalPremiumAmount" type="number" step="0.01" min="0"
                      className="form-text required form-control"
                      {...register("totalPremiumAmount", {
                        required: "Total Premium Amount is required.",
                        validate: (val) => {
                          if (val === "" || isNaN(Number(val))) return "Must be a valid amount.";
                          if (Number(val) < 0) return "Amount must be 0 or greater.";
                          return true;
                        },
                      })} />
                    <ErrorRow errors={errors} name="totalPremiumAmount" />
                  </div>
                </div>

                {/* RDS Question 1 */}
                <div className="row form-group">
                  <div className="col-sm-6 col-md-6 form-item form-group">
                    <label htmlFor="rds1" className="control-label js-form-required form-required">Does the employer group currently provide retiree drug coverage (other than Group MedicareBlue Rx) to their retirees?</label>
                    <select id="rds1" className="form-select form-control"
                      {...register("rds1", {
                        required: "RDS Question 1 is required.",
                        onChange: (e) => {
                          if (e.target.value !== "No") {
                            setValue("rds2", "");
                            setValue("rds3", "");
                            clearErrors(["rds2", "rds3"]);
                          }
                        },
                      })}>
                      <option value="">Select</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                    {watchRDS1 === "No" && (
                      <div style={noteStyle}>
                        Vendor must conduct outreach to any retirees currently being claimed by another source under Retiree Drug Subsidy (RDS).
                      </div>
                    )}
                    <ErrorRow errors={errors} name="rds1" />
                  </div>
                  <div className="col-sm-6 col-md-6" />
                </div>

                {/* RDS Question 2 (Q1 = No) */}
                {showRDS2 && (
                  <div className="row form-group">
                    <div className="col-sm-6 col-md-6 form-item form-group">
                      <label htmlFor="rds2" className="control-label js-form-required form-required">Does the employer group currently claim their retirees for the Retiree Drug Subsidy (RDS)?</label>
                      <select id="rds2" className="form-select form-control"
                        {...register("rds2", {
                          validate: (val) =>
                            getValues("rds1") === "No" && !val
                              ? "RDS Question 2 is required."
                              : true,
                          onChange: (e) => {
                            if (e.target.value !== "No") {
                              setValue("rds3", "");
                              clearErrors("rds3");
                            }
                          },
                        })}>
                        <option value="">Select</option>
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                      </select>
                      {watchRDS2 === "No" && (
                        <div style={noteStyle}>
                          Vendor must conduct outreach to any retirees currently being claimed by another source under Retiree Drug Subsidy (RDS).
                        </div>
                      )}
                      <ErrorRow errors={errors} name="rds2" />
                    </div>
                    <div className="col-sm-6 col-md-6" />
                  </div>
                )}

                {/* RDS Question 3 (Q1 = No and Q2 = No) */}
                {showRDS3 && (
                  <div className="row form-group">
                    <div className="col-sm-6 col-md-6 form-item form-group">
                      <label htmlFor="rds3" className="control-label js-form-required form-required">Is this group completely replacing the retiree drug coverage it currently provides with Group MedicareBlue Rx coverage?</label>
                      <select id="rds3" className="form-select form-control"
                        {...register("rds3", {
                          validate: (val) =>
                            getValues("rds1") === "No" && getValues("rds2") === "No" && !val
                              ? "RDS Question 3 is required."
                              : true,
                        })}>
                        <option value="">Select</option>
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                      </select>
                      <div style={noteStyle}>
                        Vendor must conduct outreach to any retirees who are currently being claimed under the RDS.
                      </div>
                      <ErrorRow errors={errors} name="rds3" />
                    </div>
                    <div className="col-sm-6 col-md-6" />
                  </div>
                )}

                {/* Creditable Drug Coverage */}
                <div className="row form-group">
                  <div className="col-sm-6 col-md-6 form-item form-group">
                    <label htmlFor="creditableDrugCoverage" className="control-label js-form-required form-required">Creditable Drug Coverage Indicator</label>
                    <select id="creditableDrugCoverage" className="form-select form-control"
                      {...register("creditableDrugCoverage", { required: "Creditable Drug Coverage Indicator is required." })}>
                      <option value="">Select</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                    {watchCred === "No" && (
                      <div style={noteStyle}>Coverage Gap of 63 or more days.</div>
                    )}
                    <ErrorRow errors={errors} name="creditableDrugCoverage" />
                  </div>
                  <div className="col-sm-6 col-md-6" />
                </div>
              </div>

              {/* ══════════════════════════════════════════════════════════════
                  BROKER INFORMATION
              ══════════════════════════════════════════════════════════════ */}
              <div style={sectionStyle}>
                <h2 style={sectionHeadingStyle}>Broker Information</h2>

                {/* First Name · Last Name */}
                <div className="row form-group">
                  <div className="col-sm-6 col-md-6 form-item form-group">
                    <label htmlFor="brokerFirstName" className="control-label">Broker First Name</label>
                    <input id="brokerFirstName" type="text" className="form-text form-control"
                      {...register("brokerFirstName")} />
                    <ErrorRow errors={errors} name="brokerFirstName" />
                  </div>
                  <div className="col-sm-6 col-md-6 form-item form-group">
                    <label htmlFor="brokerLastName" className="control-label">Broker Last Name</label>
                    <input id="brokerLastName" type="text" className="form-text form-control"
                      {...register("brokerLastName")} />
                    <ErrorRow errors={errors} name="brokerLastName" />
                  </div>
                </div>

                {/* Broker Number · Phone */}
                <div className="row form-group">
                  <div className="col-sm-6 col-md-6 form-item form-group">
                    <label htmlFor="brokerNumber" className="control-label">Broker Number</label>
                    <input id="brokerNumber" type="text" className="form-text form-control"
                      {...register("brokerNumber")} />
                    <ErrorRow errors={errors} name="brokerNumber" />
                  </div>
                  <div className="col-sm-6 col-md-6 form-item form-group">
                    <label htmlFor="brokerPhone" className="control-label">Broker's Telephone Number</label>
                    <CSPhoneInput
                      name="brokerPhone"
                      id="brokerPhone"
                      value={watchBrokerPhone ?? ""}
                      onChange={() => {}}
                      control={control}
                      rules={{}}
                    />
                    <ErrorRow errors={errors} name="brokerPhone" />
                  </div>
                </div>

                {/* Agency Number */}
                <div className="row form-group">
                  <div className="col-sm-6 col-md-6 form-item form-group">
                    <label htmlFor="agencyNumber" className="control-label">Agency Number</label>
                    <input id="agencyNumber" type="text" className="form-text form-control"
                      {...register("agencyNumber")} />
                    <ErrorRow errors={errors} name="agencyNumber" />
                  </div>
                  <div className="col-sm-6 col-md-6" />
                </div>
              </div>

              {/* ══════════════════════════════════════════════════════════════
                  PRIMARY BILLING CONTACT
              ══════════════════════════════════════════════════════════════ */}
              <div style={sectionStyle}>
                <h2 style={sectionHeadingStyle}>Primary Billing Contact</h2>
                {/* <p style={subtitleStyle}>Contact Type: Primary Billing Contact &nbsp;·&nbsp; Contact Reason: New</p> */}

                {/* First Name · Last Name */}
                <div className="row form-group">
                  <div className="col-sm-6 col-md-6 form-item form-group">
                    <label htmlFor="pbcFirstName" className="control-label js-form-required form-required">First Name</label>
                    <input id="pbcFirstName" type="text" className="form-text required form-control"
                      {...register("pbcFirstName", { required: "First Name is required." })} />
                    <ErrorRow errors={errors} name="pbcFirstName" />
                  </div>
                  <div className="col-sm-6 col-md-6 form-item form-group">
                    <label htmlFor="pbcLastName" className="control-label js-form-required form-required">Last Name</label>
                    <input id="pbcLastName" type="text" className="form-text required form-control"
                      {...register("pbcLastName", { required: "Last Name is required." })} />
                    <ErrorRow errors={errors} name="pbcLastName" />
                  </div>
                </div>

                {/* Effective Date · Expiration Date */}
                <div className="row form-group">
                  <div className="col-sm-6 col-md-6 form-item form-group">
                    <label htmlFor="pbcEffectiveDate" className="control-label js-form-required form-required">Contact Effective Date</label>
                    <input id="pbcEffectiveDate" type="date" className="form-text required form-control"
                      {...register("pbcEffectiveDate", { required: "Contact Effective Date is required." })} />
                    <ErrorRow errors={errors} name="pbcEffectiveDate" />
                  </div>
                  <div className="col-sm-6 col-md-6 form-item form-group">
                    <label htmlFor="pbcExpirationDate" className="control-label">Contact Expiration Date</label>
                    <input id="pbcExpirationDate" type="date" className="form-text form-control"
                      {...register("pbcExpirationDate", {
                        validate: (val) => {
                          if (!val) return true;
                          const eff = getValues("pbcEffectiveDate");
                          if (eff && new Date(val) < new Date(eff)) return "Expiration Date cannot be earlier than Effective Date.";
                          return true;
                        },
                      })} />
                    <ErrorRow errors={errors} name="pbcExpirationDate" />
                  </div>
                </div>

                {/* Phone · Email */}
                <div className="row form-group">
                  <div className="col-sm-6 col-md-6 form-item form-group">
                    <label htmlFor="pbcPhone" className="control-label js-form-required form-required">Phone Number</label>
                    <CSPhoneInput
                      name="pbcPhone"
                      id="pbcPhone"
                      value={watchPbcPhone ?? ""}
                      onChange={() => {}}
                      control={control}
                      rules={{ required: "Phone Number is required." }}
                    />
                    <ErrorRow errors={errors} name="pbcPhone" />
                  </div>
                  <div className="col-sm-6 col-md-6 form-item form-group">
                    <label htmlFor="pbcEmail" className="control-label js-form-required form-required">Email Address</label>
                    <input id="pbcEmail" type="email" className="form-text required form-control"
                      {...register("pbcEmail", {
                        required: "Email Address is required for Primary Billing Contact.",
                        validate: (val) => {
                          if (val?.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val))
                            return "Please enter a valid email address.";
                          return true;
                        },
                      })} />
                    <ErrorRow errors={errors} name="pbcEmail" />
                  </div>
                </div>
              </div>

              {/* ══════════════════════════════════════════════════════════════
                  EMPLOYER HEADQUARTERS CONTACT
              ══════════════════════════════════════════════════════════════ */}
              <div style={sectionStyle}>
                <h2 style={sectionHeadingStyle}>Employer Headquarters Contact</h2>
                {/* <p style={subtitleStyle}>Contact Type: Employer Group Plan Headquarters &nbsp;·&nbsp; Contact Reason: New</p> */}

                {/* First Name · Last Name */}
                <div className="row form-group">
                  <div className="col-sm-6 col-md-6 form-item form-group">
                    <label htmlFor="hqFirstName" className="control-label js-form-required form-required">First Name</label>
                    <input id="hqFirstName" type="text" className="form-text required form-control"
                      {...register("hqFirstName", { required: "First Name is required." })} />
                    <ErrorRow errors={errors} name="hqFirstName" />
                  </div>
                  <div className="col-sm-6 col-md-6 form-item form-group">
                    <label htmlFor="hqLastName" className="control-label js-form-required form-required">Last Name</label>
                    <input id="hqLastName" type="text" className="form-text required form-control"
                      {...register("hqLastName", { required: "Last Name is required." })} />
                    <ErrorRow errors={errors} name="hqLastName" />
                  </div>
                </div>

                {/* Effective Date · Expiration Date */}
                <div className="row form-group">
                  <div className="col-sm-6 col-md-6 form-item form-group">
                    <label htmlFor="hqEffectiveDate" className="control-label js-form-required form-required">Contact Effective Date</label>
                    <input id="hqEffectiveDate" type="date" className="form-text required form-control"
                      {...register("hqEffectiveDate", { required: "Contact Effective Date is required." })} />
                    <ErrorRow errors={errors} name="hqEffectiveDate" />
                  </div>
                  <div className="col-sm-6 col-md-6 form-item form-group">
                    <label htmlFor="hqExpirationDate" className="control-label">Contact Expiration Date</label>
                    <input id="hqExpirationDate" type="date" className="form-text form-control"
                      {...register("hqExpirationDate", {
                        validate: (val) => {
                          if (!val) return true;
                          const eff = getValues("hqEffectiveDate");
                          if (eff && new Date(val) < new Date(eff)) return "Expiration Date cannot be earlier than Effective Date.";
                          return true;
                        },
                      })} />
                    <ErrorRow errors={errors} name="hqExpirationDate" />
                  </div>
                </div>

                {/* Address · Address 2 */}
                <div className="row form-group">
                  <div className="col-sm-6 col-md-6 form-item form-group">
                    <label htmlFor="hqAddress1" className="control-label js-form-required form-required">Address</label>
                    <input id="hqAddress1" type="text" className="form-text required form-control"
                      {...register("hqAddress1", { required: "Address is required." })} />
                    <ErrorRow errors={errors} name="hqAddress1" />
                  </div>
                  <div className="col-sm-6 col-md-6 form-item form-group">
                    <label htmlFor="hqAddress2" className="control-label">Address 2</label>
                    <input id="hqAddress2" type="text" className="form-text form-control" {...register("hqAddress2")} />
                  </div>
                </div>

                {/* City · State */}
                <div className="row form-group">
                  <div className="col-sm-6 col-md-6 form-item form-group">
                    <label htmlFor="hqCity" className="control-label js-form-required form-required">City</label>
                    <input id="hqCity" type="text" className="form-text required form-control"
                      {...register("hqCity", {
                        required: "City is required.",
                        pattern: {
                          value: /^[A-Za-z\s\-']+$/,
                          message: "City must contain only letters, spaces, hyphens, or apostrophes.",
                        },
                      })} />
                    <ErrorRow errors={errors} name="hqCity" />
                  </div>
                  <div className="col-sm-6 col-md-6 form-item form-group">
                    <label htmlFor="hqState" className="control-label js-form-required form-required">State</label>
                    <select id="hqState" className="form-select form-control"
                      {...register("hqState", { required: "State is required." })}>
                      <option value="">Select</option>
                      {HQ_STATES.map(({ value, label }) => <option key={value} value={value}>{label}</option>)}
                    </select>
                    <ErrorRow errors={errors} name="hqState" />
                  </div>
                </div>

                {/* Zip · Phone */}
                <div className="row form-group">
                  <div className="col-sm-6 col-md-6 form-item form-group">
                    <label htmlFor="hqZip" className="control-label js-form-required form-required">Zip Code</label>
                    <input id="hqZip" type="text" className="form-text required form-control"
                      {...register("hqZip", {
                        required: "Zip Code is required.",
                        pattern: {
                          value: /^\d{5}(-\d{4})?$/,
                          message: "Enter a 5-digit ZIP or ZIP+4 (e.g., 55121 or 55121-1234).",
                        },
                      })} />
                    {/* <div style={hintStyle}>5-digit ZIP or ZIP+4 (e.g., 55121 or 55121-1234)</div> */}
                    <ErrorRow errors={errors} name="hqZip" />
                  </div>
                  <div className="col-sm-6 col-md-6 form-item form-group">
                    <label htmlFor="hqPhone" className="control-label js-form-required form-required">Phone Number</label>
                    <CSPhoneInput
                      name="hqPhone"
                      id="hqPhone"
                      value={watchHqPhone ?? ""}
                      onChange={() => {}}
                      control={control}
                      rules={{ required: "Phone Number is required." }}
                    />
                    <ErrorRow errors={errors} name="hqPhone" />
                  </div>
                </div>

                {/* Email */}
                <div className="form-item form-group">
                  <label htmlFor="hqEmail" className="control-label">Email Address</label>
                  <input id="hqEmail" type="email" className="form-text form-control"
                    {...register("hqEmail", {
                      validate: (val) => {
                        if (val?.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val))
                          return "Please enter a valid email address.";
                        return true;
                      },
                    })} />
                  <ErrorRow errors={errors} name="hqEmail" />
                </div>
              </div>

              {/* ── Dialogs ──────────────────────────────────────────────── */}
              {csMessage}
              {csDialog}

              {/* ── Submit ───────────────────────────────────────────────── */}
              <Button
                type="submit"
                className="webform-button--next button js-form-submit form-submit btn-default btn"
                disabled={isProcessing}
                style={{ marginTop: "32px" }}
              >
                {isProcessing ? "Submitting…" : "Submit"}
              </Button>

            </form>
          </div>
        </section>
      </div>
    </div>
  );
};

export default GroupImplementation;
